import React, { useContext } from "react";
import { Checkbox } from "primereact/checkbox";
import { InputNumber } from "primereact/inputnumber";
import { SettingsContext } from "./SettingsContext.tsx";
import { MAX_SPIRAL_SECTIONS, ProblemFormatOptions, SPIRAL_SECTION_FORMATS } from "../Constants.ts";
import { labelFor } from "../Labels.ts";
import { useTranslation } from "../../Shared/i18n/useTranslation.ts";
import { format as formatText } from "../../Shared/i18n/format.ts";

/**
 * The sections of a spiral-review sheet: a format and how many problems of it,
 * three or four of them stacked down the page (ADR 0013). Long division and word
 * problems are not offered - see SPIRAL_SECTION_FORMATS.
 */
export function SpiralReviewSettings() {
    const { settings, setSettings } = useContext(SettingsContext);
    const sections = settings.SpiralSections ?? [];
    const t = useTranslation();

    function countFor(format: ProblemFormatOptions): number | null {
        return sections.find((s) => s.format === format)?.count ?? null;
    }

    function toggle(format: ProblemFormatOptions) {
        const present = sections.some((s) => s.format === format);
        if (present) {
            update(sections.filter((s) => s.format !== format));
        } else if (sections.length < MAX_SPIRAL_SECTIONS) {
            update([...sections, { format, count: 4 }]);
        }
    }

    function setCount(format: ProblemFormatOptions, count: number) {
        update(
            sections.map((s) => (s.format === format ? { ...s, count: count || 1 } : s))
        );
    }

    function update(next: { format: ProblemFormatOptions; count: number }[]) {
        setSettings({ ...settings, SpiralSections: next });
    }

    return (
        <div className="spiralReviewSettings">
            <span className="settingLabel">
                {formatText(t('settings.sectionsUpTo'), { max: MAX_SPIRAL_SECTIONS })}
            </span>
            {SPIRAL_SECTION_FORMATS.map((format) => {
                const count = countFor(format);
                const checked = count !== null;
                return (
                    <div key={format} className="spiralSectionRow">
                        <Checkbox
                            inputId={`spiral-${format}`}
                            checked={checked}
                            onChange={() => toggle(format)}
                        />
                        <label htmlFor={`spiral-${format}`}>{labelFor(format, settings.Language)}</label>
                        {checked && (
                            <InputNumber
                                className="spiralCountInput"
                                value={count}
                                min={1}
                                max={16}
                                showButtons
                                onChange={(e) => setCount(format, e.value ?? 1)}
                                pt={{
                                    incrementButton: { "aria-label": `${t('settings.more')}${labelFor(format, settings.Language)}` },
                                    decrementButton: { "aria-label": `${t('settings.fewer')}${labelFor(format, settings.Language)}` },
                                }}
                            />
                        )}
                    </div>
                );
            })}
        </div>
    );
}

export default SpiralReviewSettings;
